import { createApi } from "@reduxjs/toolkit/query/react";
import { useDispatch } from "react-redux";
import { getUnixTs } from "@bridgesplit/utils";

import {
    ROUTE_GET_USER_TOTAL_BY_TYPE,
    ROUTE_GET_USER_POINTS_CALC,
    ROUTE_REFER_USER,
    ROUTE_GET_USER_REFERRAL_POINTS_CALC,
    ROUTE_GET_USER_REFERRAL_INFO_ROUTE
} from "../constants";
import { abfSerializeQueryArgs, rubyBaseQuery } from "./util";
import { PointsRewardType, UserPoints, UserReferralInfo } from "../types";

const POINTS_TAG = "Points";
const REFERRAL_TAG = "REFERRAL";

export const pointsApi = createApi({
    reducerPath: "pointsApi",
    tagTypes: [POINTS_TAG, REFERRAL_TAG],
    baseQuery: rubyBaseQuery,
    serializeQueryArgs: abfSerializeQueryArgs,
    endpoints: (builder) => ({
        userPoints: builder.query<UserPoints, string>({
            providesTags: [POINTS_TAG],
            query() {
                return {
                    url: ROUTE_GET_USER_POINTS_CALC,
                    method: "GET",
                    params: {
                        // calc points 5s in future to prevent having to wait for one time events to end
                        timestamp: getUnixTs() + 5
                    }
                };
            }
        }),
        userReferralPoints: builder.query<UserPoints, string>({
            providesTags: [POINTS_TAG],
            query() {
                return {
                    url: ROUTE_GET_USER_REFERRAL_POINTS_CALC,
                    method: "GET",
                    params: {
                        // calc points 5s in future to prevent having to wait for one time events to end
                        timestamp: getUnixTs()
                    }
                };
            }
        }),

        userReferralInfo: builder.query<UserReferralInfo, string>({
            providesTags: [REFERRAL_TAG],
            queryFn: async (params, api, extraOptions, baseQuery) => {
                const referralInfoRes = await baseQuery({
                    url: ROUTE_GET_USER_REFERRAL_INFO_ROUTE,
                    method: "GET"
                });

                const referralInfo = referralInfoRes.data as UserReferralInfo;
                return { data: referralInfo };
            }
        }),
        // take in user ID to trigger refetch
        pointsTotalForReward: builder.query<number, { pointsReward: PointsRewardType; userId: string }>({
            query({ pointsReward }) {
                return {
                    url: `${ROUTE_GET_USER_TOTAL_BY_TYPE}/${pointsReward}`,
                    method: "GET"
                };
            },
            transformResponse: ({ points }: { points: number }) => points
        }),
        applyReferral: builder.mutation<string, { code: string; transaction: string }>({
            invalidatesTags: [REFERRAL_TAG, POINTS_TAG],
            query({ code, transaction }) {
                return {
                    url: ROUTE_REFER_USER,
                    headers: {
                        "Wallet-Transaction": transaction
                    },
                    params: { referralCode: code },
                    method: "POST"
                };
            }
        })
    })
});

export const {
    useUserPointsQuery,
    useUserReferralPointsQuery,
    useUserReferralInfoQuery,
    usePointsTotalForRewardQuery,
    useApplyReferralMutation
} = pointsApi;

export function usePointsApi() {
    const dispatch = useDispatch();

    return {
        resetPoints: () => dispatch(pointsApi.util.invalidateTags([POINTS_TAG])),
        resetPointsAndReferrals: () => dispatch(pointsApi.util.invalidateTags([POINTS_TAG, REFERRAL_TAG])),
        resetReferrals: () => dispatch(pointsApi.util.invalidateTags([REFERRAL_TAG]))
    };
}
